import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "#/components/shared/card";
import Typography from "#/components/shared/typography";
import { ArticleReview } from "#/services/articleReviews";
import useArticlesList from "#/admin/articles/hooks/useArticlesList";
import { ArticleListItem } from "#/services/articles";
import useArticleReviewsArticles from "#/admin/article-reviews/hooks/useArticleReviewsArticles";
import useArticleReviewsList from "#/admin/article-reviews/hooks/useArticleReviewsList";
import { formatDate } from "#/components/utls/time";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { requestArticleReviewRevision } from "#/services/articles";
import { useToast } from "#/hooks/ToastContext";

function sortReviews(items: ArticleReview[]) {
  return [...items].sort((a, b) => {
    const aDate =
      new Date(
        (a as { created_at?: string }).created_at ||
          (a as { updated_at?: string }).updated_at ||
          (a as { createdAt?: string }).createdAt ||
          (a as { updatedAt?: string }).updatedAt ||
          0
      ).getTime();
    const bDate =
      new Date(
        (b as { created_at?: string }).created_at ||
          (b as { updated_at?: string }).updated_at ||
          (b as { createdAt?: string }).createdAt ||
          (b as { updatedAt?: string }).updatedAt ||
          0
      ).getTime();
    return bDate - aDate;
  });
}

function sortArticles(items: ArticleListItem[]) {
  return [...items].sort((a, b) => {
    const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bDate - aDate;
  });
}

type ReviewListPanelProps = {
  articleId: string;
  onViewReview: (reviewId: string) => void;
};

function ReviewListPanel({ articleId, onViewReview }: ReviewListPanelProps) {
  const listQuery = useArticleReviewsList({ articleId }, { enabled: true });
  const { showToast } = useToast();
  const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([]);
  const [isSubmittingSubset, setIsSubmittingSubset] = useState(false);
  const reviews = useMemo(() => {
    const items = Array.isArray(listQuery.data) ? listQuery.data : [];
    return sortReviews(items);
  }, [listQuery.data]);

  if (listQuery.isLoading) {
    return (
      <Typography variant="body2" className="text-brown-600">
        Loading reviews…
      </Typography>
    );
  }

  if (listQuery.isError) {
    return (
      <Typography variant="body2" className="text-terracotta">
        {listQuery.error instanceof Error
          ? listQuery.error.message
          : "Unable to load reviews."}
      </Typography>
    );
  }

  if (reviews.length === 0) {
    return (
      <Typography variant="body2" className="text-brown-600">
        No reviews found for this article.
      </Typography>
    );
  }

  const actionableReviewIds = reviews
    .filter((r) => r.status === "pending" || r.status === "warn")
    .map((r) => r.id)
    .filter(Boolean);

  const allSelected =
    actionableReviewIds.length > 0 &&
    actionableReviewIds.every((id) => selectedReviewIds.includes(id));

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedReviewIds([]);
      return;
    }
    setSelectedReviewIds(actionableReviewIds);
  };

  const handleTriggerSubset = async () => {
    if (!selectedReviewIds.length) {
      showToast("Select at least one review.", "error");
      return;
    }

    try {
      setIsSubmittingSubset(true);
      await requestArticleReviewRevision(articleId, {
        reviewIds: selectedReviewIds,
      });
      showToast("Submitted selected reviews for AI revision.", "success");
      setSelectedReviewIds([]);
      listQuery.refetch();
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Failed to submit selected reviews.",
        "error"
      );
    } finally {
      setIsSubmittingSubset(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ActionButton size="sm" tone="secondary" onClick={handleToggleAll}>
          {allSelected ? "Clear selection" : "Select all actionable"}
        </ActionButton>
        <ActionButton
          size="sm"
          onClick={handleTriggerSubset}
          isLoading={isSubmittingSubset}
          disabled={!selectedReviewIds.length || isSubmittingSubset}
        >
          Trigger AI revision for selected ({selectedReviewIds.length})
        </ActionButton>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-cream-50 text-brown-700">
            <tr>
              <th className="px-4 py-3 font-semibold">Select</th>
              <th className="px-4 py-3 font-semibold">Review ID</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Created</th>
              <th className="px-4 py-3 font-semibold">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-beige-100">
            {reviews.map((review) => {
              const isActionable =
                review.status === "pending" || review.status === "warn";
              const checked = selectedReviewIds.includes(review.id);
              return (
                <tr key={review.id}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      disabled={!isActionable}
                      checked={checked}
                      onChange={(event) => {
                        setSelectedReviewIds((current) => {
                          if (event.target.checked) {
                            return current.includes(review.id)
                              ? current
                              : [...current, review.id];
                          }
                          return current.filter((id) => id !== review.id);
                        });
                      }}
                    />
                  </td>
                  <td className="px-4 py-3 text-brown-900">{review.id}</td>
                  <td className="px-4 py-3 text-brown-700">{review.status}</td>
                  <td className="px-4 py-3 text-brown-700">
                    {formatDate(review.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <ActionButton
                      size="sm"
                      tone="secondary"
                      onClick={() => onViewReview(review.id)}
                    >
                      View details
                    </ActionButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ArticleReviewsLayout() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const articlesQuery = useArticlesList();
  const reviewsArticlesQuery = useArticleReviewsArticles();
  const selectedArticleId = searchParams.get("articleId") ?? "";

  const articles = useMemo(() => {
    const items = Array.isArray(articlesQuery.data) ? articlesQuery.data : [];
    return sortArticles(items);
  }, [articlesQuery.data]);

  const reviewSummaries = useMemo(() => {
    const items = Array.isArray(reviewsArticlesQuery.data)
      ? reviewsArticlesQuery.data
      : [];
    return [...items].sort(
      (a, b) => (b.reviewCount || 0) - (a.reviewCount || 0)
    );
  }, [reviewsArticlesQuery.data]);

  const articlesById = useMemo(() => {
    const map = new Map<string, ArticleListItem>();
    articles.forEach((article) => {
      map.set(article.id, article);
    });
    return map;
  }, [articles]);

  useEffect(() => {
    if (!selectedArticleId) return;
    if (expandedId === selectedArticleId) return;
    const hasArticle = reviewSummaries.some(
      (item) => item.articleId === selectedArticleId
    );
    if (hasArticle) setExpandedId(selectedArticleId);
  }, [expandedId, reviewSummaries, selectedArticleId]);

  const handleToggle = useCallback(
    (articleId: string) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "reviews");
      setExpandedId((current) => {
        const next = current === articleId ? null : articleId;
        if (next) {
          params.set("articleId", articleId);
        } else {
          params.delete("articleId");
        }
        setSearchParams(params);
        return next;
      });
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="flex flex-col gap-4">
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <div className="flex flex-col gap-1">
            <Typography variant="h3" className="text-brown-900">
              Article reviews
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Articles with reviews ({reviewSummaries.length}).
            </Typography>
          </div>
        </Card.Header>
        <Card.Body className="bg-white">
          {reviewsArticlesQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading review availability…
            </Typography>
          ) : reviewsArticlesQuery.isError ? (
            <Typography variant="body2" className="text-terracotta">
              {reviewsArticlesQuery.error instanceof Error
                ? reviewsArticlesQuery.error.message
                : "Unable to load review availability."}
            </Typography>
          ) : articlesQuery.isError ? (
            <Typography variant="body2" className="text-terracotta">
              {articlesQuery.error instanceof Error
                ? articlesQuery.error.message
                : "Unable to load articles."}
            </Typography>
          ) : reviewSummaries.length === 0 ? (
            <Typography variant="body2" className="text-brown-600">
              No reviews found yet.
            </Typography>
          ) : (
            <div className="flex flex-col divide-y divide-beige-100">
              {reviewSummaries.map((summary) => {
                const article = articlesById.get(summary.articleId);
                const isExpanded = expandedId === summary.articleId;
                return (
                  <div key={summary.articleId} className="py-2">
                    <button
                      type="button"
                      onClick={() => handleToggle(summary.articleId)}
                      className={`flex w-full flex-col gap-1 rounded-md px-3 py-3 text-left transition ${
                        isExpanded
                          ? "bg-cream-50 text-brown-900"
                          : "hover:bg-cream-50"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <Typography variant="body2" className="text-brown-900">
                            {article?.title || article?.slug || summary.articleId}
                          </Typography>
                          <Typography variant="caption" className="text-brown-600">
                            {article?.slug
                              ? `${article.slug}`
                              : `Article ID: ${summary.articleId}`}
                          </Typography>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="inline-flex items-center rounded-full bg-beige-100 px-2 py-1 text-xs font-semibold text-brown-700">
                            {summary.reviewCount} review
                            {summary.reviewCount === 1 ? "" : "s"}
                          </span>
                          <ActionButton
                            size="sm"
                            tone="secondary"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                `/admin/content/reviews/article/${summary.articleId}`
                              );
                            }}
                          >
                            View reviews
                          </ActionButton>
                          {summary.lastReviewedAt ? (
                            <Typography variant="caption" className="text-brown-500">
                              Last reviewed {formatDate(summary.lastReviewedAt)}
                            </Typography>
                          ) : null}
                        </div>
                      </div>
                    </button>
                    {isExpanded ? (
                      <div className="mt-3 px-3 pb-3">
                        <ReviewListPanel
                          articleId={summary.articleId}
                          onViewReview={(reviewId) =>
                            navigate(`/admin/content/reviews/${reviewId}`)
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ArticleReviewsLayout;
